import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Camera } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { PushNotifications } from '@capacitor/push-notifications';
import { Contacts } from '@capacitor-community/contacts';
import { AppService } from '@freelancer/app';
import { LocalStorage } from '@freelancer/local-storage';
import { NativeSettingsService } from '@freelancer/native-settings';
import { Pwa } from '@freelancer/pwa';
import { Tracking } from '@freelancer/tracking';
import { Microphone } from '@mozartec/capacitor-microphone';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
// This is to prevent circular dependency issues, as both this service and
// camera-input.component.ts require modals.
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { firstValueFrom } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import type {
  PermissionHanders,
  PermissionStateType,
} from './permissions.model';
import {
  PermissionDenyReason,
  PermissionStateEnum,
  PermissionType,
} from './permissions.model';
import { ModalSize } from '../modal/modal-size';
import { ModalService } from '../modal/modal.service';

@UntilDestroy({ className: 'Permissions' })
@Injectable({ providedIn: 'root' })
export class Permissions {
  private permissionsRequestDisabled = false;

  private readonly permissionHandlers: PermissionHanders = {
    [PermissionType.CAMERA]: {
      nativeCheck: async () =>
        (await Camera.checkPermissions()).camera as PermissionStateType,
      nativeRequest: async options => {
        if (options?.redirectToSettings) {
          return this.openSystemSettingsAndCheckWhenAppActive(
            PermissionType.CAMERA,
          );
        }
        return (await Camera.requestPermissions({ permissions: ['camera'] }))
          .camera as PermissionStateType;
      },
      webCheck: async () =>
        (await navigator.permissions.query({
          name: 'camera' as PermissionName,
        })) as PermissionStatus & { state: PermissionStateType },
    },

    [PermissionType.CONTACTS]: {
      nativeCheck: async () =>
        (await Contacts.checkPermissions()).contacts as PermissionStateType,
      nativeRequest: async options => {
        if (options?.redirectToSettings) {
          return this.openSystemSettingsAndCheckWhenAppActive(
            PermissionType.CONTACTS,
          );
        }
        return (await Contacts.requestPermissions())
          .contacts as PermissionStateType;
      },
      webCheck: () =>
        undefined as PermissionStatus & {
          state: PermissionStateEnum.NOT_SUPPORTED;
        },
    },

    [PermissionType.MICROPHONE]: {
      nativeCheck: async () =>
        (await Microphone.checkPermissions()).microphone as PermissionStateType,
      nativeRequest: async options => {
        if (options?.redirectToSettings) {
          return this.openSystemSettingsAndCheckWhenAppActive(
            PermissionType.MICROPHONE,
          );
        }
        return (await Microphone.requestPermissions())
          .microphone as PermissionStateType;
      },
      webCheck: async () =>
        (await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        })) as PermissionStatus & { state: PermissionStateType },
    },

    [PermissionType.PUSH_NOTIFICATIONS]: {
      nativeCheck: async () =>
        (await PushNotifications.checkPermissions())
          .receive as PermissionStateType,
      nativeRequest: async options => {
        if (options?.redirectToSettings) {
          return this.openSystemSettingsAndCheckWhenAppActive(
            PermissionType.PUSH_NOTIFICATIONS,
          );
        }
        return (await PushNotifications.requestPermissions())
          .receive as PermissionStateType;
      },
      webCheck: async () =>
        (await navigator.permissions.query({
          name: 'notifications',
        })) as PermissionStatus & { state: PermissionStateType },
      internalRequestResult: async () => {
        return firstValueFrom(
          this.localStorage.get('notificationsInternalGranted').pipe(
            map(granted => {
              return !!granted;
            }),
            untilDestroyed(this),
          ),
        );
      },
    },

    [PermissionType.GEOLOCATION]: {
      nativeCheck: async () =>
        (await Geolocation.checkPermissions()).location as PermissionStateType,
      nativeRequest: async options => {
        if (options?.redirectToSettings) {
          return this.openSystemSettingsAndCheckWhenAppActive(
            PermissionType.GEOLOCATION,
          );
        }
        return (await Geolocation.requestPermissions())
          .location as PermissionStateType;
      },
      webCheck: async () =>
        (await navigator.permissions.query({
          name: 'geolocation',
        })) as PermissionStatus & { state: PermissionStateType },
    },
  };

  constructor(
    private modalService: ModalService,
    private pwa: Pwa,
    private app: AppService,
    private tracking: Tracking,
    private localStorage: LocalStorage,
    private nativeSettings: NativeSettingsService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  /**
   * This function handles permission requests in two parts: native and browser.
   *
   * Native:
   * 1. Checks the native permission status:
   *    - If granted: sets permissionGranted = true and returns.
   *    - If prompt: shows the internal modal.
   *      After the modal closes:
   *        a) If the user approves, requests permission via the native API.
   *        b) Otherwise, sets permissionGranted = false.
   *    - If already denied: sets permissionGranted = false.
   *        a) If it is possible, redirects the user to the native settings app
   *
   * 2. Based on permissionGranted, decides whether to show the recovery modal.
   *
   * Browser:
   * 1. Tries to check the permission using the Permissions API.
   *    - This is wrapped in try/catch because some browsers (e.g. Safari) may not support the API.
   *    - If the API is unavailable: sets permissionGranted = true, allowing the browser to handle permissions in its default way.
   *
   * 2. If the API is available:
   *    - If granted:
   *      a) if internal state is denied. Show the internal request modal if possible.
   *      b) if internal state is granted, sets permissionGranted = true.
   *    - If prompt: Show the internal request modal if possible
   *      After the modal closes:
   *        a) If denied: sets permissionGranted = false.
   *        b) If accepted: sets permissionGranted = true, allowing the onchange handler to show the recovery modal if needed.
   *    - If already denied: sets permissionGranted = false.
   *
   * Again, permissionGranted is used to determine whether the recovery modal should be shown.
   *
   * @param: skipInternalRequestModal
   * Controls whether to skip the internal modal shown before the native permission request.
   * - Default is false: show the internal modal.
   * - Set to true: skip the internal modal and directly show the native permission prompt,
   *   reducing the number of modals presented to the user.
   *
   * @param: redirectToNativeSettings
   * Controls whether to redirect the user to the native settings app
   * - Default is false: do not redirect. use the system prompt.
   * - Set to true: redirect the user to the native settings app
   *
   *
   * @return boolean
   * Indicates whether the permission request was successful (true) or not (false).
   *
   * NOTE: For browser this only indicates whether the internal request modal was accepted.
   * The browser permission request has to be handled separately.
   */
  async requestPermissions({
    permission,
    reason,
    skipInternalRequestModal = false,
  }: {
    permission: PermissionType;
    reason: string;
    skipInternalRequestModal?: boolean;
  }): Promise<boolean> {
    if (this.permissionsRequestDisabled) {
      return false;
    }

    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    let permissionGranted = false;
    let internalGranted: boolean | undefined = false;
    let showInternalRequestModal = false;
    let permissionDenyReason: PermissionDenyReason | undefined;
    let redirectToSettings = false;
    const permissionStatus = await this.checkPermissions(permission);

    if (this.pwa.isNative()) {
      switch (permissionStatus) {
        case PermissionStateEnum.GRANTED: {
          permissionGranted = true;
          break;
        }
        case PermissionStateEnum.PROMPT:
        case PermissionStateEnum.PROMPT_WITH_RATIONALE: {
          showInternalRequestModal = !skipInternalRequestModal;
          if (!showInternalRequestModal) {
            internalGranted = true;
          }
          break;
        }
        case PermissionStateEnum.DENIED: {
          permissionDenyReason = PermissionDenyReason.APP;
          // User has already denied the permission. We can't show the system prompt to the user.
          // However, we can still show the internal request modal and redirect the user to the settings app
          redirectToSettings = this.nativeSettings.isSupported();
          if (redirectToSettings) {
            showInternalRequestModal = !skipInternalRequestModal;
          }
          permissionGranted = false;
          break;
        }
        default:
          break; // Do nothing
      }
      if (showInternalRequestModal) {
        const modalRes = await this.modalService
          .open('PermissionsRequestModal', {
            inputs: {
              permission,
              reason,
            },
            closeable: false,
            showBackdrop: true,
          })
          .afterClosed();

        internalGranted = modalRes;
        if (!modalRes) {
          permissionDenyReason = PermissionDenyReason.USER_DENIED_FROM_APP;
          permissionGranted = false;
          this.trackPermissionRequestUserAction({
            permission,
            reason,
            label: 'RejectFromNative',
            permissionDenyReason,
          });
        }
      }

      // User has accepted the internal request.
      // Continue to show the native prompt or redirect to the settings app.
      if (internalGranted) {
        const nativePermission = await this.permissionHandlers[
          permission
        ].nativeRequest({
          redirectToSettings,
        });
        if (nativePermission === PermissionStateEnum.GRANTED) {
          // Track if the permission was granted on mobile native.
          this.trackPermissionRequestUserAction({
            permission,
            reason,
            label: 'AcceptFromNative',
          });
          permissionGranted = true;
        } else {
          permissionDenyReason = PermissionDenyReason.APP;
          permissionGranted = false;
          this.trackPermissionRequestUserAction({
            permission,
            label: 'RejectFromNative',
            reason,
            permissionDenyReason,
          });
        }
      }
    } else {
      // non-native handling
      // `as any` is required to allow camera permissions to be requested from
      // the very same API.
      try {
        switch (permissionStatus) {
          case PermissionStateEnum.GRANTED: {
            const internalRequestResultFn =
              this.permissionHandlers[permission].internalRequestResult;
            if (internalRequestResultFn) {
              // Internal request modal might be needed
              const internalPermissionState = await internalRequestResultFn();
              if (!internalPermissionState) {
                showInternalRequestModal = !skipInternalRequestModal;
              }
            }

            if (!showInternalRequestModal) {
              permissionGranted = true;
            }
            break;
          }
          case PermissionStateEnum.PROMPT: {
            showInternalRequestModal = !skipInternalRequestModal;

            if (!showInternalRequestModal) {
              permissionGranted = true;
            }
            break;
          }
          case PermissionStateEnum.DENIED: {
            permissionDenyReason = PermissionDenyReason.APP;
            permissionGranted = false;
            break;
          }
          default:
            break; // Do nothing
        }

        if (showInternalRequestModal) {
          const modalRes = await this.modalService
            .open('PermissionsRequestModal', {
              inputs: {
                permission,
                reason,
              },
              closeable: false,
              showBackdrop: true,
              size: ModalSize.MID,
            })
            .afterClosed();
          if (!modalRes) {
            permissionDenyReason = PermissionDenyReason.USER_DENIED_FROM_APP;
            permissionGranted = false;
            this.trackPermissionRequestUserAction({
              permission,
              reason,
              label: 'RejectFromBrowser',
              permissionDenyReason,
            });
          } else {
            this.registerRecoveryModalForWebPermissionChanges({
              permission,
              reason,
            });
            permissionGranted = true;
          }
        }
      } catch {
        // In the case that we're working with a browser that does not have
        // the permissions query, we'll let it slide and continue on.
        // This usually would catch that case.
        this.trackPermissionRequestUserAction({
          permission,
          reason,
          label: 'unknown',
          permissionDenyReason,
        });
        permissionGranted = true;
      }
    }

    // User has declined the internal request modal.
    // Show the recovery modal
    if (!permissionGranted) {
      // Native should trigger this if the permission was not granted.
      // Browser should trigger this if permission was denied from modal.
      this.modalService.open('PermissionsRecoveryModal', {
        inputs: {
          permission,
          permissionReason: reason,
          permissionDenyReason:
            permissionDenyReason ?? PermissionDenyReason.OTHER,
        },
        showBackdrop: true,
        size: ModalSize.MID,
      });
    }

    return permissionGranted;
  }

  async checkPermissions(
    permission: PermissionType,
  ): Promise<PermissionStateType> {
    if (isPlatformBrowser(this.platformId)) {
      if (this.pwa.isNative()) {
        const capacitorPermissionStatus =
          await this.permissionHandlers[permission].nativeCheck();

        return capacitorPermissionStatus;
      }
      // `as any` is required to allow camera permissions to be requested from
      // the very same API.
      try {
        const windowRes = await this.permissionHandlers[permission].webCheck();

        return windowRes.state;
      } catch {
        return PermissionStateEnum.DENIED;
      }
    }
    return PermissionStateEnum.DENIED;
  }

  async registerRecoveryModalForWebPermissionChanges({
    permission,
    reason,
  }: {
    permission: PermissionType;
    reason: string;
  }): Promise<void> {
    const windowRes = await this.permissionHandlers[permission].webCheck();

    windowRes.onchange = (e: Event) => {
      const permissionDenyReason = PermissionDenyReason.APP;
      if ((e.target as any).state !== PermissionStateEnum.GRANTED) {
        // Track that the permission has been denied on the browser.
        this.trackPermissionRequestUserAction({
          permission,
          reason,
          label: 'RejectFromBrowser',
          permissionDenyReason,
        });

        this.modalService.open('PermissionsRecoveryModal', {
          inputs: {
            permission,
            permissionReason: reason,
            permissionDenyReason,
          },
          showBackdrop: true,
          size: ModalSize.MID,
        });
      } else {
        // In the case it was granted on browser, let's track that.
        this.trackPermissionRequestUserAction({
          permission,
          reason,
          label: 'AcceptFromBrowser',
        });
      }
      windowRes.onchange = () => undefined;
    };
  }

  disablePermissionRequest(): void {
    this.permissionsRequestDisabled = true;
  }

  getPermissionHandlers(): PermissionHanders {
    return this.permissionHandlers;
  }

  private async openSystemSettingsAndCheckWhenAppActive(
    type: PermissionType,
  ): Promise<PermissionStateType> {
    if (type === PermissionType.PUSH_NOTIFICATIONS) {
      this.nativeSettings.openNotificationSettings();
    } else {
      this.nativeSettings.openAppSettings();
    }
    // Wait for the app to be active before checking permissions
    await firstValueFrom(
      this.app.appState$.pipe(
        filter(state => state.isActive),
        untilDestroyed(this),
      ),
    );
    return this.permissionHandlers[type].nativeCheck();
  }

  private trackPermissionRequestUserAction({
    permission,
    reason,
    label,
    permissionDenyReason,
  }: {
    permission: PermissionType;
    reason: string;
    label:
      | 'AcceptFromNative'
      | 'RejectFromNative'
      | 'AcceptFromBrowser'
      | 'RejectFromBrowser'
      | 'unknown';
    permissionDenyReason?: PermissionDenyReason;
  }): void {
    this.tracking.track('user_action', {
      section: 'GeneralPermissions',
      subsection: 'PermissionsRequestModal',
      label,
      action: 'click',
      extra_params: {
        permission,
        permission_reason: reason,
        ...(permissionDenyReason
          ? {
              permission_deny_reason: permissionDenyReason,
            }
          : {}),
      },
    });
  }
}
